import { createContext, ReactNode, useContext, useMemo } from 'react'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  EventActivitiesPlanningInfo,
  getEventActivityPlanningKey,
} from 'app/frontend/base/api/event'
import { EventActivityParticipantEntry } from '../components/eventActivityParticipantEntry'
import { resolveEventParticipantEntries } from '../components/eventActivityParticipants'
import {
  EventActivityIssues,
  resolveEventActivityIssues,
} from '../components/eventActivityIssues'
import { useTeamCharacterDetails } from 'app/frontend/base/config/queries'

type TeamEventParticipantsContextValue = {
  allEntries: EventActivityParticipantEntry[]
  activityIssuesById: Map<number, EventActivityIssues>
  activeActivityIds: Set<number>
  planningInfoByActivityAndCharacter: Map<
    string,
    {
      killCount: number
      wishlistUpgrade: number
      edited: boolean
      wishlistItems: PlanningWishlistItem[]
    }
  >
  characterDetailsById: Record<string, wowaudit.CharacterDetails>
}

type PlanningWishlistItem =
  EventActivitiesPlanningInfo['activities'][string]['wishlist_items'][number][number]

const TeamEventParticipantsContext =
  createContext<TeamEventParticipantsContextValue | null>(null)

type TeamEventParticipantsProviderProps = {
  event: wowaudit.Event
  activities: wowaudit.EventActivity[]
  planningInfo?: EventActivitiesPlanningInfo
  children: ReactNode
}

export const TeamEventParticipantsProvider = ({
  event,
  activities,
  planningInfo,
  children,
}: TeamEventParticipantsProviderProps) => {
  const { team } = useTeam()
  const teamMembers = team.members ?? []

  const allEntries = useMemo(() => {
    const activityIds = activities.map((activity) => activity.id)
    return resolveEventParticipantEntries({
      participants: event.participants,
      activityIds,
      teamMembers,
    })
  }, [activities, event.participants, teamMembers])

  const activityIssuesById = useMemo(() => {
    const unavailableStatuses: wowaudit.EventParticipantReference['status'][] =
      ['absent', 'missing', 'removed']
    const summary = new Map<number, EventActivityIssues>()
    activities.forEach((activity) => {
      const activityPlanning =
        planningInfo?.activities[getEventActivityPlanningKey(activity)]
      summary.set(
        activity.id,
        resolveEventActivityIssues({
          activity,
          entries: allEntries,
          unavailableStatuses,
          recommendedSetup: activityPlanning?.recommended_setup,
        }),
      )
    })
    return summary
  }, [activities, allEntries, planningInfo])
  const activeActivityIds = useMemo(
    () =>
      new Set(
        activities
          .filter((activity) => activity.active)
          .map((activity) => activity.id),
      ),
    [activities],
  )

  const planningInfoByActivityAndCharacter = useMemo(() => {
    const lookup = new Map<
      string,
      {
        killCount: number
        wishlistUpgrade: number
        edited: boolean
        wishlistItems: PlanningWishlistItem[]
      }
    >()
    const planningByActivity = planningInfo?.activities
    if (!planningByActivity) return lookup

    activities.forEach((activity) => {
      const activityPlanning =
        planningByActivity[getEventActivityPlanningKey(activity)]
      const killCounts: Record<string, number> = {
        ...(activityPlanning?.participant_kill_counts || {}),
      }
      const wishlistItemsByCharacter: Record<string, PlanningWishlistItem[]> = {
        ...(activityPlanning?.wishlist_items || {}),
      }
      const characterIds = new Set([
        ...Object.keys(killCounts),
        ...Object.keys(wishlistItemsByCharacter),
      ])

      characterIds.forEach((characterId) => {
        const wishlistItems = wishlistItemsByCharacter[characterId] || []
        const wishlistUpgrade = wishlistItems.reduce(
          (sum, wishlistItem) =>
            sum +
            (typeof wishlistItem.score?.percentage === 'number'
              ? wishlistItem.score.percentage
              : 0),
          0,
        )
        const edited = wishlistItems.some((wishlistItem) => wishlistItem.edited)

        lookup.set(`${activity.id}:${characterId}`, {
          killCount: killCounts[characterId] ?? 0,
          wishlistUpgrade,
          edited,
          wishlistItems,
        })
      })
    })

    return lookup
  }, [activities, planningInfo])

  const characterIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...allEntries.map((entry) => entry.participant.characterReference.id),
          ...teamMembers.map((member) => member.characterReference.id),
        ]),
      ),
    [allEntries, teamMembers],
  )

  const { data: characterDetails } = useTeamCharacterDetails(
    team.id,
    characterIds,
    event.id.toString(),
  )

  const characterDetailsById = useMemo(
    () =>
      characterDetails?.reduce((acc, character) => {
        acc[character.id.toString()] = character
        return acc
      }, {} as Record<string, wowaudit.CharacterDetails>),
    [characterDetails],
  )

  return (
    <TeamEventParticipantsContext.Provider
      value={{
        allEntries,
        activityIssuesById,
        activeActivityIds,
        planningInfoByActivityAndCharacter,
        characterDetailsById: characterDetailsById ?? {},
      }}
    >
      {children}
    </TeamEventParticipantsContext.Provider>
  )
}

export const useTeamEventParticipantsContext = () => {
  const context = useContext(TeamEventParticipantsContext)
  if (!context) {
    throw new Error(
      'useTeamEventParticipantsContext must be used within provider',
    )
  }
  return context
}
